import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { isRivieraPath } from "@/components/CountrySwitcher";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const STORAGE_KEY = "trustline_popup_state"; // {submitted?, redeemed?, code?}

function readState() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch { return {}; }
}
function writeState(s) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}

const HIDDEN_PATH_PREFIXES = ["/admin", "/provider", "/portal", "/login", "/signup", "/reset-password", "/set-password", "/forgot-password", "/my-account", "/unsubscribe"];

// Popup currently runs the approved NL offer only; France stays off until management approves a French offer.
const countryOf = ({ pathname, search }) => (isRivieraPath(pathname) || /country=fr/i.test(search) ? "FR" : "NL");

export default function DiscountPopup() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const country = countryOf(location);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [marketing, setMarketing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [issued, setIssued] = useState(null);

  useEffect(() => {
    const st = readState();
    const blocked = HIDDEN_PATH_PREFIXES.some((p) => location.pathname.startsWith(p))
      || country !== "NL" || !!user || st.submitted || st.redeemed
      || sessionStorage.getItem("trustline_popup_dismissed");
    if (blocked) { if (!issued) setOpen(false); return undefined; }
    const tm = setTimeout(() => setOpen(true), 1500);
    return () => clearTimeout(tm);
  }, [location.pathname, country, user, issued]);

  const handleClose = () => {
    sessionStorage.setItem("trustline_popup_dismissed", "1");
    setOpen(false);
  };

  const submit = async (e) => {
    e?.preventDefault();
    const em = email.trim().toLowerCase();
    if (!em) return;
    setSubmitting(true);
    try {
      const r = await api.post("/public/discount-leads", {
        email: em, marketing_consent: marketing, source: "Website pop-up",
        country, language: (i18n.language || "en").slice(0, 2),
      });
      setIssued({ code: r.data.discount_code, emailed: r.data.email_status === "sent" });
      writeState({ submitted: true, code: r.data.discount_code });
      try { localStorage.setItem("trustline_discount_code", r.data.discount_code); } catch { /* ignore */ }
    } catch (e2) {
      if (e2?.response?.status === 409) {
        writeState({ redeemed: true });
        toast.error(t("popup.not_eligible"));
        setOpen(false);
      } else toast.error(formatError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        className="w-[calc(100vw-1.5rem)] max-w-md max-h-[calc(100dvh-1.5rem)] overflow-y-auto overscroll-contain p-0 gap-0 border-0 rounded-2xl [&>button:last-child]:hidden"
        data-testid="discount-popup"
      >
        <DialogTitle className="sr-only">{t("popup.title")}</DialogTitle>
        <DialogDescription className="sr-only">{t("popup.sr_desc")}</DialogDescription>
        <div className="sticky top-0 z-10 h-0 flex justify-end">
        <button
          type="button"
          onClick={handleClose}
          className="mt-3 mr-3 w-9 h-9 rounded-full bg-white hover:bg-slate-100 text-slate-600 flex items-center justify-center shadow-sm ring-1 ring-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1B3A6B]"
          data-testid="popup-close"
          aria-label={t("popup.close")}
        >
          <X className="w-4 h-4" />
        </button>
        </div>

        {!issued ? (
          <div className="p-6 sm:p-7">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#5BA83F]/15 text-[#3F7A1F] flex items-center justify-center" aria-hidden="true">
                <Sparkles className="w-5 h-5" />
              </div>
              <span className="text-xs uppercase tracking-[0.18em] font-bold text-[#5BA83F]">{t("popup.badge")}</span>
            </div>
            <h2 className="font-heading text-2xl font-bold text-[#1B3A6B] leading-tight">{t("popup.title")}</h2>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{t("popup.intro")}</p>

            <form onSubmit={submit} className="mt-5 space-y-3">
              <label htmlFor="popup-email" className="sr-only">{t("popup.email_label")}</label>
              <Input
                id="popup-email"
                type="email"
                required
                autoComplete="email"
                inputMode="email"
                enterKeyHint="send"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ block: "center", behavior: "smooth" }), 300)}
                placeholder={t("popup.email_ph")}
                className="h-11 text-base sm:text-sm"
                data-testid="popup-email"
              />

              <p className="text-xs text-slate-600 leading-relaxed" data-testid="popup-privacy-notice">
                {t("popup.notice")}{" "}
                <Link to="/privacy" onClick={handleClose} className="underline hover:text-[#1B3A6B]" data-testid="popup-privacy-link">{t("popup.privacy")}</Link>.
              </p>

              <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
                <Checkbox
                  checked={marketing}
                  onCheckedChange={(v) => setMarketing(!!v)}
                  className="mt-0.5"
                  data-testid="popup-marketing"
                />
                <span>{t("popup.marketing")}</span>
              </label>

              <Button
                type="submit"
                disabled={submitting || !email.trim()}
                className="w-full h-11 bg-[#1B3A6B] hover:bg-[#152D54] text-white rounded-full font-semibold"
                data-testid="popup-submit"
              >
                {submitting ? t("popup.sending") : t("popup.submit")}
              </Button>
            </form>
          </div>
        ) : (
          <div className="p-6 sm:p-7 text-center" data-testid="popup-success" role="status">
            <div className="w-14 h-14 rounded-full bg-[#5BA83F]/20 text-[#3F7A1F] flex items-center justify-center mx-auto" aria-hidden="true">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h2 className="font-heading text-xl font-bold text-[#1B3A6B] mt-4">{t("popup.thanks")}</h2>
            <p className="text-sm text-slate-600 mt-2">{t("popup.your_code")}</p>
            <div className="mt-3 inline-block bg-[#E8F5E0] ring-1 ring-[#5BA83F]/40 text-[#3F7A1F] px-5 py-2 rounded-full font-mono font-bold text-lg tracking-wider" data-testid="popup-code">
              {issued.code}
            </div>
            <p className="text-xs text-slate-500 mt-3" data-testid="popup-email-status">{issued.emailed ? t("popup.emailed") : t("popup.not_emailed")}</p>
            <p className="text-xs text-slate-500 mt-1">{t("popup.use")}</p>
            <div className="mt-5 flex flex-wrap gap-2 justify-center">
              <Button asChild className="bg-[#5BA83F] hover:bg-[#4A8A33] text-white rounded-full">
                <Link to="/book" onClick={() => setOpen(false)} data-testid="popup-book">{t("popup.book")}</Link>
              </Button>
              <Button variant="outline" onClick={handleClose} className="rounded-full" data-testid="popup-later">{t("popup.later")}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
